import { Effect, EffectProperties, EffectFields } from '../../core/Effect';
import { forwardRef } from 'react';
import * as THREE from 'three';
import { Field } from '@/types/core/Field';
import { BlendFunction } from 'postprocessing';

export interface ScanlineEffectProperties extends EffectProperties {
  density: number;
}

// Create a reusable ScanlineEffect component that can be used in both preview and world
const ScanlineEffectComponent = forwardRef<any, { properties: ScanlineEffectProperties; isPreview?: boolean }>((props, ref) => {
  const { properties, isPreview } = props;
  
  // IMPORTANT: Return null immediately in preview mode to prevent WebGL errors
  if (isPreview) {
    return null;
  }
  
  const { density, blendFunction } = properties;

  // Convert blendFunction string to number if it exists
  const blendFunctionValue = blendFunction ? 
    BlendFunction[blendFunction as keyof typeof BlendFunction] : 
    BlendFunction.OVERLAY;

  // NOTE: The ScanlineEffect component is not directly accessible in the current setup.
  // This is a placeholder implementation that would need to be customized for your specific use case.
  // For now, we'll return null to avoid errors
  return null;
});

export const ScanlineEffectItem: Effect<ScanlineEffectProperties> = {
  id: '',
  name: 'Scanline',
  type: 'effect',
  subtype: 'ScanlineEffect',
  icon: 'Effect',
  properties: {
    density: 1.25,
    blendFunction: 'OVERLAY',
  },
  fields: [
    ...EffectFields,
    {
      key: 'properties.density',
      type: 'number',
      label: 'Density',
      default: 1.25,
      min: 0.1,
      max: 3,
      step: 0.05,
      panel: 'effect',
    },
    {
      key: 'properties.blendFunction',
      type: 'select',
      label: 'Blend Function',
      default: 'OVERLAY',
      options: [
        { value: 'NORMAL', label: 'Normal' },
        { value: 'ADD', label: 'Add' },
        { value: 'SUBTRACT', label: 'Subtract' },
        { value: 'MULTIPLY', label: 'Multiply' },
        { value: 'SCREEN', label: 'Screen' },
        { value: 'OVERLAY', label: 'Overlay' },
      ],
      panel: 'effect',
    }
  ],
  render: (properties: ScanlineEffectProperties, isPreview: boolean, ref: React.RefObject<THREE.Object3D | null>) => {
    return <ScanlineEffectComponent properties={properties} isPreview={isPreview} ref={ref as any} />;
  }
}; 